import styles from "./AppointmentForm.module.scss";
import { useFormik } from "formik";
import validationSchema from "../../../models/AppointmentSchema";
import InputCustom from "../../InputCustom/InputCustom";
import SlotBookingForm from "../SlotBookingForm/SlotBookingForm";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchRates } from "../../../api/rates";
import { fetchServicesByRateId } from "../../../api/services";
import Chip from "../../Chip/Chip";
import { addAppointment } from "../../../api/appointment";
import Button from "../../Button/Button";
import { fetchAddressSuggestions } from "../../../api/address";
import ConsentModal from "../../../pages/AppointmentPage/components/ConsentModal/ConsentModal";
import Honeypot from "../Honeypot/Honeypot";
import { toast } from "react-toastify";

function debounce(func, delay) {
  let timer;

  const debouncedFunction = function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };

  debouncedFunction.cancel = () => {
    clearTimeout(timer);
  };

  return debouncedFunction;
}

function AppointmentForm() {
  const [rates, setRates] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedRate, setSelectedRate] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [address, setAddress] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const addressRef = useRef(null);

  const [consentGiven, setConsentGiven] = useState(false);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [honeypot, setHoneypot] = useState("");

  async function onSubmit(formValues) {
    if (honeypot) {
      return;
    }

    if (!consentGiven) {
      toast.error(
        "Merci d'accepter la politique de confidentialité avant l'envoi.",
      );
      return;
    }

    const payload = {
      ...formValues,
      website: honeypot,
      services: formValues.services.map(({ rateId, serviceId }) => ({
        rateId,
        serviceId,
      })),
      prestations: formValues.services.map(
        ({ rateName, serviceTitle }) => `${rateName} : ${serviceTitle}`,
      ),
    };

    try {
      const response = await addAppointment(payload);

      if (response?.status !== 200) {
        throw new Error("La création du rendez-vous a échoué.");
      }

      toast.success(
        "Votre demande de rendez-vous a été envoyée avec succès. Nous vous recontacterons sous peu !",
      );

      formik.resetForm();
      setSelectedRate("");
      setSelectedService("");
      setServices([]);
      setAddress("");
      setAddressSuggestions([]);
      setConsentGiven(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Une erreur est survenue.",
      );
    }
  }

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      address: "",
      email: "",
      phoneNumber: "",
      selectedDate: "",
      selectedSlot: "",
      services: [],
    },
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchRates();
        setRates(response.data);
      } catch (error) {
        console.error("Erreur lors du chargement des tarifs : ", error);
        toast.error("Impossible de charger les types de prestations.");
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!selectedRate) {
        setServices([]);
        return;
      }

      try {
        const response = await fetchServicesByRateId(selectedRate);
        if (!cancelled) {
          setServices(response.data);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Erreur lors du chargement des prestations : ", error);
          setServices([]);
          toast.error("Impossible de charger les prestations.");
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [selectedRate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressRef.current && !addressRef.current.contains(event.target)) {
        setAddressSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchAddressSuggestionsDebounced = useMemo(
    () =>
      debounce(async (inputText) => {
        try {
          const suggestions = await fetchAddressSuggestions(inputText);
          setAddressSuggestions(suggestions);
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des suggestions d'adresse : ",
            error,
          );
          toast.error(
            "Erreur lors de la récupération des suggestions d'adresse.",
          );
        }
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      fetchAddressSuggestionsDebounced.cancel();
    };
  }, [fetchAddressSuggestionsDebounced]);

  const selectAddress = (selectedAddress) => {
    formik.setFieldValue("address", selectedAddress);
    setAddress(selectedAddress);
    setAddressSuggestions([]);
  };

  const handleRateChange = (event) => {
    setSelectedRate(event.target.value);
    setSelectedService("");
  };

  const handleServiceChange = (event) => {
    setSelectedService(event.target.value);
  };

  const handleDateChange = (date) => {
    formik.setFieldValue("selectedDate", date);
  };

  const handleSlotChange = (slot) => {
    formik.setFieldValue("selectedSlot", slot);
  };

  const addChip = () => {
    if (!selectedRate || !selectedService) {
      return;
    }

    const rate = rates.find((item) => item.id.toString() === selectedRate);
    const service = services.find(
      (item) => item.id.toString() === selectedService,
    );

    if (!rate || !service) {
      return;
    }

    const newService = {
      rateId: rate.id,
      serviceId: service.id,
      rateName: rate.name,
      serviceTitle: service.title,
    };

    formik.setValues((previousValues) => ({
      ...previousValues,
      services: [...previousValues.services, newService],
    }));

    setSelectedRate("");
    setSelectedService("");
    setServices([]);
  };

  const removeChip = (index) => {
    formik.setFieldValue(
      "services",
      formik.values.services.filter(
        (_, serviceIndex) => serviceIndex !== index,
      ),
    );
  };

  return (
    <div className={styles.container}>
      <h2>Formulaire de rendez-vous</h2>
      <form onSubmit={formik.handleSubmit}>
        <div className={styles.coordinate}>
          <h3 className={styles.title}>Coordonnées</h3>
          <div className={styles.row}>
            <div>
              <InputCustom
                id="lastName"
                type="text"
                name="lastName"
                placeholder="Nom"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.lastName}
                style={{ marginTop: "10px" }}
              />
              {formik.touched.lastName && formik.errors.lastName ? (
                <div className={styles.error}>{formik.errors.lastName}</div>
              ) : null}
            </div>
            <div>
              <InputCustom
                id="firstName"
                type="text"
                name="firstName"
                placeholder="Prénom"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.firstName}
                style={{ marginTop: "10px" }}
              />
              {formik.touched.firstName && formik.errors.firstName ? (
                <div className={styles.error}>{formik.errors.firstName}</div>
              ) : null}
            </div>
          </div>
          <div className={styles.autocompleteContainer} ref={addressRef}>
            <InputCustom
              id="address"
              type="text"
              name="address"
              placeholder="3 rue de l'exemple, 59300 Valenciennes"
              onChange={(event) => {
                const inputValue = event.target.value;

                setAddress(inputValue);
                formik.setFieldValue("address", inputValue);

                if (inputValue.length >= 6) {
                  fetchAddressSuggestionsDebounced(inputValue);
                } else {
                  setAddressSuggestions([]);
                }
              }}
              onBlur={formik.handleBlur}
              value={address}
              style={{ marginTop: "20px" }}
            />
            {formik.touched.address && formik.errors.address ? (
              <div className={styles.error}>{formik.errors.address}</div>
            ) : null}
            {addressSuggestions.length > 0 && (
              <div className={styles.dropdownMenu}>
                {addressSuggestions.map((suggestion) => (
                  <div
                    key={suggestion.osm_id}
                    className={styles.dropdownItem}
                    onClick={() => selectAddress(suggestion.address)}
                  >
                    {suggestion.address}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <InputCustom
              id="email"
              type="text"
              name="email"
              placeholder="Email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              style={{ marginTop: "20px" }}
            />
            {formik.touched.email && formik.errors.email ? (
              <div className={styles.error}>{formik.errors.email}</div>
            ) : null}
          </div>
          <div>
            <InputCustom
              id="phoneNumber"
              type="tel"
              name="phoneNumber"
              placeholder="Numéro de téléphone"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.phoneNumber}
              style={{ marginTop: "20px" }}
            />
            {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
              <div className={styles.error}>{formik.errors.phoneNumber}</div>
            ) : null}
          </div>
        </div>

        <div className={styles.addService}>
          <h3 className={styles.title}>Ajouter une prestation</h3>
          <div>
            <select
              name="selectedRate"
              onChange={handleRateChange}
              value={selectedRate}
            >
              <option value="">Sélectionner un type de prestation</option>
              {rates.map((rate) => (
                <option key={rate.id} value={rate.id}>
                  {rate.name}
                </option>
              ))}
            </select>
          </div>
          {selectedRate && (
            <div>
              <select
                name="selectedService"
                onChange={handleServiceChange}
                value={selectedService}
              >
                <option value="">Sélectionner une prestation</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          {selectedRate && selectedService && (
            <div className={styles.btnContainer}>
              <Button
                type="button"
                color="var(--primary-color)"
                onClick={addChip}
              >
                Ajouter
              </Button>
            </div>
          )}
        </div>

        {formik.values.services.map((service, index) => (
          <Chip
            key={`${service.rateId}-${service.serviceId}-${index}`}
            label={`${service.rateName} - ${service.serviceTitle}`}
            onDelete={() => removeChip(index)}
          />
        ))}

        {typeof formik.errors.services === "string" &&
          formik.submitCount > 0 && (
            <div className={styles.error}>{formik.errors.services}</div>
          )}

        <div className={styles.addSlot}>
          <h3 className={styles.title}>Choisir un créneau</h3>
          <SlotBookingForm
            selectedDate={formik.values.selectedDate}
            selectedSlot={formik.values.selectedSlot}
            onDateChange={handleDateChange}
            onSlotChange={handleSlotChange}
          />
        </div>
        {formik.touched.selectedSlot && formik.errors.selectedSlot ? (
          <div className={styles.error}>{formik.errors.selectedSlot}</div>
        ) : null}
        <div className={styles.consentLine}>
          <input
            type="checkbox"
            id="consent"
            checked={consentGiven}
            onChange={(e) => setConsentGiven(e.target.checked)}
          />
          <label htmlFor="consent">
            J'ai pris connaissance de la{" "}
            <button type="button" onClick={() => setConsentModalOpen(true)}>
              politique de confidentialité
            </button>
          </label>
        </div>

        <ConsentModal
          isOpen={consentModalOpen}
          onClose={() => setConsentModalOpen(false)}
        />

        <Honeypot
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />

        <div className={styles.btnContainer}>
          <Button
            disabled={formik.isSubmitting}
            type="submit"
            color="var(--primary-color)"
          >
            {formik.isSubmitting ? "Envoi en cours..." : "Envoyer"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default AppointmentForm;
